/**
 * Module multi-select with friendly Finnish labels (matches Sidebar config).
 * Kategorisoitu helpomman valinnan vuoksi.
 */
import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export const ALL_MODULES = [
  // Ydintoiminnot
  { key: "invoicing", label: "Laskutus", group: "Ydin" },
  { key: "accounting", label: "Kirjanpito", group: "Ydin" },
  { key: "ereceipt", label: "E-Kuitti", group: "Ydin" },
  { key: "crm", label: "CRM", group: "Ydin" },
  { key: "quotes", label: "Tarjoukset", group: "Ydin" },

  // Henkilöstö & varasto
  { key: "hr", label: "HR & Palkat (palkkaus.fi-tyyppinen)", group: "Henkilöstö" },
  { key: "shifts", label: "Työvuorot & Keikat", group: "Henkilöstö" },
  { key: "inventory", label: "Varasto", group: "Henkilöstö" },
  { key: "projects", label: "Projektit", group: "Henkilöstö" },

  // Liikenne & kuljetus
  { key: "logistics", label: "Logistiikka", group: "Kuljetus" },
  { key: "fleet", label: "Kalusto", group: "Kuljetus" },
  { key: "taxi", label: "Taksi", group: "Kuljetus" },
  { key: "car_rental", label: "Autovuokraus", group: "Kuljetus" },
  { key: "auto_repair", label: "Autokorjaamo", group: "Kuljetus" },

  // Palvelu
  { key: "restaurant", label: "Ravintola", group: "Palvelu" },
  { key: "money_transfer", label: "Rahansiirto", group: "Palvelu" },
  { key: "field_service", label: "Kenttätyö", group: "Palvelu" },
  { key: "rental", label: "Vuokraus (kalusto)", group: "Palvelu" },
  { key: "cleaning", label: "Siivouspalvelut", group: "Palvelu" },
  { key: "home_care", label: "Kotipalvelu", group: "Palvelu" },
  { key: "food_beverage", label: "Catering / F&B", group: "Palvelu" },

  // Asiantuntija
  { key: "psa", label: "Asiantuntijapalvelut", group: "Asiantuntija" },
  { key: "advanced_crm", label: "Edistynyt CRM", group: "Asiantuntija" },
  { key: "tasks", label: "Tehtävät", group: "Asiantuntija" },
  { key: "meeting", label: "Kokoushuone", group: "Asiantuntija" },

  // Hallinto & laatu
  { key: "construction", label: "Rakennusprojektit", group: "Hallinto" },
  { key: "property_management", label: "Kiinteistöhallinta", group: "Hallinto" },
  { key: "event_management", label: "Tapahtumat", group: "Hallinto" },
  { key: "dms", label: "Dokumenttihallinta", group: "Hallinto" },
  { key: "qms", label: "Laadunhallinta", group: "Hallinto" },
  { key: "reporting", label: "Kuljetusraportointi", group: "Hallinto" },
];

const GROUPS = ["Ydin", "Henkilöstö", "Kuljetus", "Palvelu", "Asiantuntija", "Hallinto"];

export default function ModuleSelector({ value = [], onChange, disabled = false }) {
  const toggle = (key) => {
    if (disabled) return;
    const set = new Set(value);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    onChange?.([...set]);
  };
  const selectAll = () => onChange?.(ALL_MODULES.map((m) => m.key));
  const clear = () => onChange?.([]);

  return (
    <div className="space-y-3" data-testid="module-selector">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Valittu <strong className="text-amber-200">{value.length}</strong> / {ALL_MODULES.length} moduulia
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={selectAll}
            className="text-[11px] text-amber-300 hover:underline"
            data-testid="module-select-all"
          >
            Valitse kaikki
          </button>
          <span className="text-zinc-600">·</span>
          <button
            type="button"
            onClick={clear}
            className="text-[11px] text-muted-foreground hover:underline"
            data-testid="module-clear-all"
          >
            Tyhjennä
          </button>
        </div>
      </div>

      {GROUPS.map((g) => {
        const items = ALL_MODULES.filter((m) => m.group === g);
        if (items.length === 0) return null;
        return (
          <div key={g} className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{g}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
              {items.map((m) => {
                const active = value.includes(m.key);
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => toggle(m.key)}
                    disabled={disabled}
                    data-testid={`module-toggle-${m.key}`}
                    className={cn(
                      "flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border transition-colors text-left",
                      active
                        ? "border-amber-500 bg-amber-500/10 text-amber-100"
                        : "border-zinc-700 hover:border-amber-500/40 text-muted-foreground",
                    )}
                  >
                    <span
                      className={cn(
                        "w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center",
                        active ? "bg-amber-500 border-amber-500" : "border-zinc-600",
                      )}
                    >
                      {active && <Check className="w-2.5 h-2.5 text-zinc-900" strokeWidth={3} />}
                    </span>
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
